import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import { getImageUrl } from '../utils/cdnUtils'
import truemoneyLogo from '../assets/truemoney-logo.png'
import { useAuth } from '../contexts/AuthContext'
import { useUserInfo } from '../contexts/UserInfoContext'
import {
  getDepositAccountSourceMode,
  isPromptPayDepositAccount,
  isTrueMoneyDepositAccount,
  publicDepositAccountService,
  PublicDepositAccount,
  resolveDepositAccountForAmount,
} from '../services/depositAccountService'
import { getDepositFlowConfig, type DepositFlowConfig } from '../services/depositFlowService'
import { pendingDepositDraftService, type PendingDepositSenderAccount } from '../services/pendingDepositDraftService'
import { slipStorageService } from '../services/slipStorageService'
import { walletTransactionService, type WalletDepositResponse } from '../services/walletTransactionService'
import { getTenantId } from '../utils/tenantUtils'
import { buildPromptPayQrValue } from '../utils/promptPayQr'
import toast from 'react-hot-toast'
import type { DepositMethod } from './TopupPage'

const createDraftId = () => `deposit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const formatSecondsAsCountdown = (seconds: number): string => {
  const safeSeconds = Math.max(0, Math.floor(seconds))
  const minutes = Math.floor(safeSeconds / 60)
  const remainingSeconds = safeSeconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`
}

const resolvePaymentTransactionAmount = (
  transaction: WalletDepositResponse['data'] | null | undefined,
  fallbackAmount: string | number = ''
): string => {
  const rawAmount = transaction?.metadata?.paymentGateway?.requestedAmount ?? transaction?.amount ?? fallbackAmount
  const numericAmount = Number(rawAmount)
  if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
    return String(fallbackAmount || '')
  }
  return String(numericAmount)
}

const resolvePaymentReuseExpiresAtMs = (paymentGateway: Record<string, unknown> | null | undefined): number => {
  const providerCreatedAtMs = new Date(
    String(paymentGateway?.providerCreatedAt || paymentGateway?.createdAt || '')
  ).getTime()
  const reuseWindowMinutes = Number(paymentGateway?.reuseWindowMinutes || 0)
  if (Number.isFinite(providerCreatedAtMs) && providerCreatedAtMs > 0 && Number.isFinite(reuseWindowMinutes) && reuseWindowMinutes > 0) {
    return providerCreatedAtMs + (reuseWindowMinutes * 60 * 1000)
  }

  const fallbackExpiresAtMs = new Date(String(paymentGateway?.reuseExpiresAt || '')).getTime()
  return Number.isFinite(fallbackExpiresAtMs) ? fallbackExpiresAtMs : 0
}

const buildSenderAccountSnapshot = (account?: {
  bankCode?: string
  bankName?: string
  accountNumber?: string
  accountName?: string
} | null): PendingDepositSenderAccount | undefined => {
  if (!account) return undefined
  return {
    bankCode: account.bankCode || '',
    bankName: account.bankName || '',
    accountNumber: account.accountNumber || '',
    accountName: account.accountName || '',
  }
}

const resolveLiveDepositAccount = (
  accounts: PublicDepositAccount[],
  account?: PublicDepositAccount | null
): PublicDepositAccount | null => {
  if (!account) return null
  return accounts.find((item) => item._id === account._id)
    || accounts.find((item) =>
      item.accountNumber === account.accountNumber
      && String(item.bankCode || '').toUpperCase() === String(account.bankCode || '').toUpperCase()
    )
    || null
}

const DEFAULT_DEPOSIT_FLOW: DepositFlowConfig = {
  tenantId: '',
  tenantName: '',
  paymentServiceSelected: false,
  paymentGatewayEnabled: false,
  slipServiceSelected: false,
  slipVerificationEnabled: false,
  effectiveDepositMode: 'manual',
  depositAutoCreditEnabled: true,
  depositAutoCreditViaPayment: true,
  depositAutoCreditViaSlip: true,
  depositAutoCreditViaSms: true,
  automationSettingsWarning: null,
}

const TopupConfirmPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { user } = useAuth()
  const { getPrimaryBankAccount } = useUserInfo()
  const userRecord = user as (typeof user & { id?: string; _id?: string }) | null
  const userId = userRecord?.id || userRecord?._id || ''
  const [timeLeft, setTimeLeft] = useState(300) // 5 minutes in seconds
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const skipValidation = false
  const [isDragOver, setIsDragOver] = useState(false)
  const [depositAccounts, setDepositAccounts] = useState<PublicDepositAccount[]>([])
  const [depositAccountsLoaded, setDepositAccountsLoaded] = useState(false)
  const [depositFlow, setDepositFlow] = useState<DepositFlowConfig>(DEFAULT_DEPOSIT_FLOW)
  const [flowLoaded, setFlowLoaded] = useState(false)
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [paymentError, setPaymentError] = useState('')
  const [paymentTransaction, setPaymentTransaction] = useState<WalletDepositResponse['data'] | null>(null)
  const [paymentRestoreChecked, setPaymentRestoreChecked] = useState(false)
  const [countdownNowMs, setCountdownNowMs] = useState(() => Date.now())
  const [submittingPaymentSlip, setSubmittingPaymentSlip] = useState(false)
  const [submittingManualSlip, setSubmittingManualSlip] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const tenantId = user?.tenantId || getTenantId(user || undefined) || ''
  const draftIdFromUrl = searchParams.get('draft')?.trim() || ''
  const restoredDraft = useMemo(() => {
    if (!tenantId || !userId || !draftIdFromUrl) return null
    return pendingDepositDraftService.getById(tenantId, userId, draftIdFromUrl)
  }, [draftIdFromUrl, tenantId, userId])
  const [draftId, setDraftId] = useState(draftIdFromUrl)

  const amount = location.state?.amount || restoredDraft?.amount || '100'
  const depositAccount =
    (location.state?.depositAccount as PublicDepositAccount | undefined) || restoredDraft?.depositAccount
  const depositMethod =
    (location.state?.depositMethod as DepositMethod | undefined) || restoredDraft?.depositMethod || 'bank_transfer'
  const explicitPaymentGatewaySelection =
    typeof location.state?.usesPaymentGateway === 'boolean'
      ? location.state.usesPaymentGateway
      : typeof restoredDraft?.usesPaymentGateway === 'boolean'
        ? restoredDraft.usesPaymentGateway
        : undefined
  const fallbackNotice = String(location.state?.fallbackNotice || '').trim()
  const showReservationCountdown = depositMethod === 'decimal_reservation'
  const paymentGatewayEnabled = depositFlow.paymentGatewayEnabled === true
  const stateSenderAccount = location.state?.senderAccount as PendingDepositSenderAccount | undefined
  const isPaymentMode =
    depositMethod === 'bank_transfer'
    && (
      typeof explicitPaymentGatewaySelection === 'boolean'
        ? explicitPaymentGatewaySelection && (!flowLoaded || paymentGatewayEnabled)
        : (flowLoaded && paymentGatewayEnabled)
    )
  const resolvedDepositAccount = useMemo(() => {
    if (depositMethod !== 'bank_transfer') return depositAccount
    if (depositAccount && depositAccountsLoaded) {
      return resolveLiveDepositAccount(depositAccounts, depositAccount)
    }
    if (depositAccount) return depositAccount
    if (depositMethod !== 'bank_transfer' || isPaymentMode || !depositAccountsLoaded) return depositAccount

    const numericAmount = Number(amount)
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) return depositAccount

    return resolveDepositAccountForAmount(depositAccounts, numericAmount, { channel: 'bank' }) || depositAccount
  }, [amount, depositAccount, depositAccounts, depositAccountsLoaded, depositMethod, isPaymentMode])
  const isPromptPayMode = !isPaymentMode && depositMethod === 'prompt_pay'
  const isTrueMoneyMode = !isPaymentMode && depositMethod === 'true_money'
  const hasValidPromptPayAccount = Boolean(resolvedDepositAccount && isPromptPayDepositAccount(resolvedDepositAccount))
  const hasValidTrueMoneyAccount = Boolean(resolvedDepositAccount && isTrueMoneyDepositAccount(resolvedDepositAccount))
  const hasValidManualBankAccount = Boolean(
    resolvedDepositAccount && !isPromptPayDepositAccount(resolvedDepositAccount) && !isTrueMoneyDepositAccount(resolvedDepositAccount)
  )
  const selectedDepositSourceMode = useMemo(
    () => getDepositAccountSourceMode(resolvedDepositAccount),
    [resolvedDepositAccount]
  )
  const isSmsOnlyManualDeposit = !isPaymentMode && selectedDepositSourceMode === 'sms_only'
  const isSlipOnlyManualDeposit = !isPaymentMode && selectedDepositSourceMode === 'slip_only'
  const allowManualSlipAttachment = isPaymentMode || !isSmsOnlyManualDeposit
  const submitWithoutSlipAllowed = !isPaymentMode && isSmsOnlyManualDeposit
  const paymentGateway = paymentTransaction?.metadata?.paymentGateway
  const activePaymentAmountValue = useMemo(
    () => resolvePaymentTransactionAmount(paymentTransaction, amount),
    [amount, paymentTransaction]
  )
  const activePaymentAmountDisplay = activePaymentAmountValue && !Number.isNaN(Number(activePaymentAmountValue))
    ? `${Number(activePaymentAmountValue).toLocaleString()} บาท`
    : `${Number(amount || 0).toLocaleString()} บาท`
  const paymentQrImage = paymentGateway?.qrImage || ''
  const paymentQrRawData = paymentGateway?.qrRawData || paymentGateway?.qrcodePayload?.qrcode_text || ''
  const paymentQrLink = paymentGateway?.qrUrl || paymentGateway?.qrcodePayload?.link || ''
  const hasPaymentQr = Boolean(paymentQrImage || paymentQrRawData)
  const paymentReuseExpiresAtMs = useMemo(() => {
    return resolvePaymentReuseExpiresAtMs(paymentGateway as Record<string, unknown> | undefined)
  }, [paymentGateway])
  const paymentReuseSecondsLeft = paymentReuseExpiresAtMs > 0
    ? Math.max(0, Math.floor((paymentReuseExpiresAtMs - countdownNowMs) / 1000))
    : 0
  const paymentReuseCountdownText = formatSecondsAsCountdown(paymentReuseSecondsLeft)
  const savedPaymentTransactionId = restoredDraft?.paymentTransactionId?.trim() || ''
  const promptPayQrValue = useMemo(
    () => (isPromptPayMode ? buildPromptPayQrValue(resolvedDepositAccount?.accountNumber || '', amount) : null),
    [amount, isPromptPayMode, resolvedDepositAccount?.accountNumber]
  )

  useEffect(() => {
    if (paymentReuseSecondsLeft <= 0) return
    const intervalId = window.setInterval(() => setCountdownNowMs(Date.now()), 1000)
    return () => window.clearInterval(intervalId)
  }, [paymentReuseSecondsLeft])

  // Get user's primary bank account with resolved bank image
  const userBankAccount = getPrimaryBankAccount()
  const selectedSenderAccount = stateSenderAccount
    || restoredDraft?.senderAccount
    || buildSenderAccountSnapshot(userBankAccount)
  
  // // Debug logging
  // console.log('🔍 TopupConfirmPage Debug:', {
  //   userInfo: userInfo ? 'loaded' : 'not loaded',
  //   bankAccounts: userInfo?.bankAccount?.length || 0,
  //   userBankAccount: userBankAccount ? {
  //     bankName: userBankAccount.bankName,
  //     accountNumber: userBankAccount.accountNumber,
  //     accountName: userBankAccount.accountName
  //   } : 'null'
  // })

  // User info is now provided by UserInfoContext

  useEffect(() => {
    if (!draftIdFromUrl) return
    setDraftId(draftIdFromUrl)
  }, [draftIdFromUrl])

  useEffect(() => {
    if (!tenantId) {
      setDepositAccountsLoaded(true)
      setFlowLoaded(true)
      return
    }

    publicDepositAccountService.getActiveDepositAccounts(tenantId, { forceRefresh: true }).then((accounts) => {
      setDepositAccounts(Array.isArray(accounts) ? accounts : [])
      setDepositAccountsLoaded(true)
    })

    getDepositFlowConfig(tenantId)
      .then((flow) => {
        setDepositFlow(flow)
      })
      .finally(() => {
        setFlowLoaded(true)
      })
  }, [tenantId])

  useEffect(() => {
    if (!tenantId || !userId) return
    if (!amount || (!resolvedDepositAccount && !isPaymentMode)) return

    const nextDraftId = draftId || createDraftId()
    pendingDepositDraftService.upsert(tenantId, userId, {
      id: nextDraftId,
      amount: String(amount),
      depositMethod,
      depositAccount: resolvedDepositAccount || undefined,
      senderAccount: selectedSenderAccount,
      usesPaymentGateway: isPaymentMode,
    })

    if (nextDraftId !== draftId) {
      setDraftId(nextDraftId)
    }

    if (draftIdFromUrl !== nextDraftId) {
      navigate(`/topup-confirm?draft=${encodeURIComponent(nextDraftId)}`, {
        replace: true,
        state: location.state,
      })
    }
  }, [amount, depositMethod, draftId, draftIdFromUrl, isPaymentMode, location.state, navigate, resolvedDepositAccount, selectedSenderAccount, tenantId, userId])

  useEffect(() => {
    if (!flowLoaded) return
    if (location.state?.depositAccount || restoredDraft || resolvedDepositAccount) return
    if (draftIdFromUrl && (!tenantId || !userId)) return
    if (isPaymentMode) return
    if (!depositAccountsLoaded && depositMethod === 'bank_transfer') return

    toast.error('ไม่พบรายการฝากที่ต้องแนบสลิป')
    navigate('/transaction', { replace: true })
  }, [depositAccountsLoaded, depositMethod, draftIdFromUrl, flowLoaded, isPaymentMode, location.state?.depositAccount, navigate, resolvedDepositAccount, restoredDraft, tenantId, userId])

  useEffect(() => {
    if (!flowLoaded || isPaymentMode || !resolvedDepositAccount) return

    const isInvalidPromptPay = isPromptPayMode && !hasValidPromptPayAccount
    const isInvalidTrueMoney = isTrueMoneyMode && !hasValidTrueMoneyAccount
    const isInvalidDecimalReservation = depositMethod === 'decimal_reservation' && !hasValidManualBankAccount

    if (!(isInvalidPromptPay || isInvalidTrueMoney || isInvalidDecimalReservation)) return

    toast.error('ข้อมูลบัญชีฝากไม่ตรงกับช่องทางที่เลือก')
    navigate('/topup', { replace: true })
  }, [
    depositMethod,
    flowLoaded,
    hasValidManualBankAccount,
    hasValidPromptPayAccount,
    hasValidTrueMoneyAccount,
    isPaymentMode,
    isPromptPayMode,
    isTrueMoneyMode,
    navigate,
    resolvedDepositAccount,
  ])

  useEffect(() => {
    if (!flowLoaded) return
    if (!isPaymentMode || !tenantId || !userId || !draftId) {
      setPaymentRestoreChecked(true)
      return
    }
    if (!savedPaymentTransactionId) {
      setPaymentRestoreChecked(true)
      return
    }

    let active = true
    setPaymentRestoreChecked(false)
    setPaymentError('')

    walletTransactionService.getTransactionDetails(savedPaymentTransactionId)
      .then((result) => {
        if (!active) return

        const existingPaymentTransaction = result.data?.transaction as WalletDepositResponse['data'] | null | undefined
        const canReuseExistingPayment =
          Boolean(existingPaymentTransaction?._id)
          && existingPaymentTransaction?.type === 'deposit'
          && Boolean(existingPaymentTransaction?.metadata?.paymentGateway)
          && !['failed', 'cancelled', 'expired'].includes(String(existingPaymentTransaction?.status || '').toLowerCase())

        if (canReuseExistingPayment) {
          setPaymentTransaction(existingPaymentTransaction as NonNullable<WalletDepositResponse['data']>)
          return
        }

        if (restoredDraft) {
          pendingDepositDraftService.upsert(tenantId, userId, {
            id: draftId,
            amount: restoredDraft.amount,
            depositMethod: restoredDraft.depositMethod,
            depositAccount: restoredDraft.depositAccount,
            senderAccount: restoredDraft.senderAccount,
            usesPaymentGateway: restoredDraft.usesPaymentGateway,
            paymentTransactionId: '',
            paymentExternalRefId: '',
            createdAt: restoredDraft.createdAt,
          })
        }
      })
      .finally(() => {
        if (active) setPaymentRestoreChecked(true)
      })

    return () => {
      active = false
    }
  }, [draftId, flowLoaded, isPaymentMode, restoredDraft, savedPaymentTransactionId, tenantId, userId])

  useEffect(() => {
    if (!showReservationCountdown) return

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [showReservationCountdown])

  // Clean up preview URL when component unmounts or file changes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success('คัดลอกเรียบร้อยแล้ว!')
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = text
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      toast.success('คัดลอกเรียบร้อยแล้ว!')
    }
  }

  const fallbackToManualBank = async (response: WalletDepositResponse) => {
    if (!tenantId || !userId) return false

    const numericAmount = Number(amount)
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) return false

    const availableDepositAccounts = depositAccounts.length > 0
      ? depositAccounts
      : await publicDepositAccountService.getActiveDepositAccounts(tenantId)
    const fallbackDepositAccount = resolveDepositAccountForAmount(availableDepositAccounts, numericAmount, { channel: 'bank' })
    if (!fallbackDepositAccount) {
      return false
    }

    const nextDraftId = createDraftId()
    pendingDepositDraftService.upsert(tenantId, userId, {
      id: nextDraftId,
      amount: String(amount),
      depositMethod: 'bank_transfer',
      depositAccount: fallbackDepositAccount,
      senderAccount: selectedSenderAccount,
      usesPaymentGateway: false,
      createdAt: restoredDraft?.createdAt,
    })
    if (draftId) {
      pendingDepositDraftService.remove(tenantId, userId, draftId)
    }

    setPaymentTransaction(null)
    setPaymentError('')
    toast.success(response.fallback?.userMessage || 'ระบบสลับไปใช้การฝากผ่านธนาคารแทนให้อัตโนมัติ')
    navigate(`/topup-confirm?draft=${encodeURIComponent(nextDraftId)}`, {
      replace: true,
      state: {
        amount: String(amount),
        depositAccount: fallbackDepositAccount,
        depositMethod: 'bank_transfer',
        senderAccount: selectedSenderAccount,
        usesPaymentGateway: false,
        fallbackNotice: response.fallback?.userMessage || response.message || 'ระบบสลับไปใช้การฝากผ่านธนาคารแทนให้อัตโนมัติ',
      }
    })

    return true
  }

  const requestPaymentQr = async () => {
    if (!tenantId || !userId || !draftId || !amount || Number(amount) <= 0) {
      setPaymentError('ข้อมูลรายการฝากไม่ครบถ้วน')
      return
    }
    if (!selectedSenderAccount?.bankCode || !selectedSenderAccount?.accountNumber) {
      setPaymentError('ไม่พบบัญชีธนาคารของผู้ใช้')
      return
    }

    setPaymentLoading(true)
    setPaymentError('')
    try {
      const response = await walletTransactionService.createDeposit({
        amount: Number(amount),
        paymentMethod: 'payment_gateway',
        reference: draftId,
        metadata: {
          note: 'user-classic created payment QR request',
          depositMethod,
          sourceChannel: 'payment',
          paymentChannel: 'PPQR',
          userMobile: user?.mobile,
          userBankAccount: {
            bankCode: selectedSenderAccount.bankCode,
            bankName: selectedSenderAccount.bankName,
            accountNumber: selectedSenderAccount.accountNumber,
            accountName: selectedSenderAccount.accountName,
          },
        },
      })

      if (!response.success || !response.data) {
        if (response.fallback?.allowed && await fallbackToManualBank(response)) {
          return
        }
        throw new Error(response.message || 'สร้าง QR ชำระเงินไม่สำเร็จ')
      }

      setPaymentTransaction(response.data)
      if (tenantId && userId && draftId) {
        pendingDepositDraftService.upsert(tenantId, userId, {
          id: draftId,
          amount: resolvePaymentTransactionAmount(response.data, amount),
          depositMethod,
          depositAccount: resolvedDepositAccount || undefined,
          senderAccount: selectedSenderAccount,
          usesPaymentGateway: true,
          paymentTransactionId: response.data._id,
          paymentExternalRefId: response.data.metadata?.paymentGateway?.externalRefId || '',
          createdAt: restoredDraft?.createdAt,
        })
      }
    } catch (error: any) {
      setPaymentTransaction(null)
      setPaymentError(error?.message || 'สร้าง QR ชำระเงินไม่สำเร็จ')
    } finally {
      setPaymentLoading(false)
    }
  }

  useEffect(() => {
    if (!flowLoaded || !paymentRestoreChecked) return
    if (!isPaymentMode || paymentTransaction || paymentLoading || paymentError) return
    if (!selectedSenderAccount || !draftId) return
    void requestPaymentQr()
  }, [draftId, flowLoaded, isPaymentMode, paymentError, paymentLoading, paymentRestoreChecked, paymentTransaction, selectedSenderAccount])

  useEffect(() => {
    if (!flowLoaded || isPaymentMode || resolvedDepositAccount || !depositAccountsLoaded) return
    toast.error('ไม่พบรายการฝากที่ต้องแนบสลิป')
    navigate('/transaction', { replace: true })
  }, [depositAccountsLoaded, flowLoaded, isPaymentMode, navigate, resolvedDepositAccount])

  const handleAttachPaymentSlip = async () => {
    if (!selectedFile) {
      toast.error('กรุณาแนบสลิป')
      return
    }
    if (!tenantId || !userId || !user?.username) {
      toast.error('ไม่พบข้อมูลผู้ใช้')
      return
    }
    if (!paymentTransaction?._id) {
      toast.error('ไม่พบรายการชำระผ่านธนาคาร')
      return
    }

    setSubmittingPaymentSlip(true)
    try {
      const transferTime = new Date()
      const uploadResult = await slipStorageService.uploadSlip(tenantId, {
        userId,
        username: user.username,
        transferAmount: parseFloat(activePaymentAmountValue),
        transferTime,
        transferDate: transferTime,
        slipFile: selectedFile
      })

      if (!uploadResult.success) {
        throw new Error(uploadResult.message || 'อัปโหลดสลิปไม่สำเร็จ')
      }

      const uploadData = Array.isArray(uploadResult.data) ? uploadResult.data[0] : uploadResult.data
      const attachResult = await walletTransactionService.attachSlipToDepositTransaction(paymentTransaction._id, {
        slipData: {
          imageUrl: String(uploadData?.storageUrl || ''),
          bankCode: userBankAccount?.bankCode,
          accountNumber: userBankAccount?.accountNumber
        }
      })

      if (!attachResult.success) {
        throw new Error(attachResult.message || 'แนบสลิปไม่สำเร็จ')
      }

      if (draftId && tenantId && userId) {
        pendingDepositDraftService.remove(tenantId, userId, draftId)
      }

      toast.success('แนบสลิปเรียบร้อยแล้ว')
      navigate('/transaction')
    } catch (error: any) {
      toast.error(error?.message || 'เกิดข้อผิดพลาดในการแนบสลิป')
    } finally {
      setSubmittingPaymentSlip(false)
    }
  }

  const submitManualDeposit = async (options?: { uploadedSlipUrl?: string; sourceChannel?: 'slip' | 'sms' }) => {
    if (!userId || !user?.username) {
      toast.error('ไม่พบข้อมูลผู้ใช้')
      return false
    }
    if (!tenantId) {
      toast.error('ไม่พบ tenant ของผู้ใช้')
      return false
    }
    if (!resolvedDepositAccount) {
      toast.error('ไม่พบบัญชีฝาก')
      return false
    }

    const uploadedSlipUrl = String(options?.uploadedSlipUrl || '')
    if (uploadedSlipUrl && isSmsOnlyManualDeposit) {
      toast.error('บัญชีรับฝากนี้ตั้งค่าเป็น SMS เท่านั้น จึงไม่สามารถแนบสลิปได้')
      return false
    }
    if (!uploadedSlipUrl && isSlipOnlyManualDeposit) {
      toast.error('บัญชีรับฝากนี้ตั้งค่าเป็น Slip เท่านั้น กรุณาแนบสลิปก่อนส่งรายการ')
      return false
    }
    const depositResult = await walletTransactionService.createDeposit({
      amount: parseFloat(amount),
      paymentMethod: 'bank_transfer',
      reference: draftId || undefined,
      ...(uploadedSlipUrl
        ? {
            slipData: {
              imageUrl: uploadedSlipUrl,
              bankCode: resolvedDepositAccount.bankCode,
              accountNumber: resolvedDepositAccount.accountNumber,
            },
          }
        : {}),
      metadata: {
        note: uploadedSlipUrl
          ? 'user-classic submitted deposit with slip attachment'
          : 'user-classic submitted deposit without slip attachment',
        depositMethod,
        selectedDepositAccountId: resolvedDepositAccount._id,
        selectedDepositAccountNumber: resolvedDepositAccount.accountNumber,
        selectedDepositAccountName: resolvedDepositAccount.accountName,
        selectedDepositBankCode: resolvedDepositAccount.bankCode,
        selectedDepositBankName: resolvedDepositAccount.bankName,
        selectedDepositSourceMode,
        senderAccount: selectedSenderAccount,
        receiverAccount: {
          accountId: resolvedDepositAccount._id,
          bankCode: resolvedDepositAccount.bankCode,
          bankName: resolvedDepositAccount.bankName,
          accountNumber: resolvedDepositAccount.accountNumber,
          accountName: resolvedDepositAccount.accountName,
          depositSourceMode: selectedDepositSourceMode,
        },
        sourceChannel: options?.sourceChannel || (uploadedSlipUrl ? 'slip' : 'sms'),
        submittedSlipImageUrl: uploadedSlipUrl || undefined,
      },
    })

    if (!depositResult.success) {
      throw new Error(depositResult.message || 'ส่งรายการฝากไม่สำเร็จ')
    }

    const returnedReference = String(depositResult.data?.details?.reference || '').trim()
    if (!depositResult.recoveredExistingDeposit && draftId && returnedReference && returnedReference !== draftId) {
      throw new Error('สลิปนี้ถูกผูกกับรายการฝากเดิมในระบบแล้ว กรุณาตรวจสอบประวัติรายการฝากหรือใช้สลิปใหม่')
    }

    if (draftId) {
      pendingDepositDraftService.remove(tenantId, userId, draftId)
    }

    toast.success(
      depositResult.recoveredExistingDeposit
        ? 'ระบบพบรายการฝากเดิมของคุณที่ส่งไว้แล้ว และรายการยังรอการตรวจสอบอยู่'
        : depositResult.message || (uploadedSlipUrl
          ? 'ยืนยันการโอนเงินเรียบร้อยแล้ว ระบบส่งรายการเข้าคิวตรวจสอบแล้ว'
          : 'ส่งรายการฝากเรียบร้อยแล้ว สามารถกลับมาแนบสลิปภายหลังได้')
    )

    navigate(uploadedSlipUrl ? '/dashboard' : '/transaction')
    return true
  }

  const handleConfirmWithoutSlip = async () => {
    if (!submitWithoutSlipAllowed) {
      toast.error('รายการฝากนี้ต้องแนบสลิปก่อนส่ง')
      return
    }

    try {
      await submitManualDeposit({ sourceChannel: 'sms' })
    } catch (error: any) {
      console.error('❌ Submit deposit without slip failed:', error)
      toast.error(error?.message || 'เกิดข้อผิดพลาดในการส่งรายการฝาก')
    }
  }

  const handleConfirm = async () => {
    if (submittingManualSlip) {
      return
    }
    if (!allowManualSlipAttachment) {
      toast.error('บัญชีรับฝากนี้ตั้งค่าเป็น SMS เท่านั้น กรุณาส่งรายการโดยไม่แนบสลิป')
      return
    }
    if (!selectedFile) {
      toast.error('กรุณาแนบสลิป')
      return
    }
    if (!userId || !user?.username) {
      toast.error('ไม่พบข้อมูลผู้ใช้')
      return
    }
    if (!tenantId) {
      toast.error('ไม่พบ tenant ของผู้ใช้')
      return
    }

    try {
      setSubmittingManualSlip(true)
      const transferTime = new Date()

      // Upload slip to S3
      const uploadResult = await slipStorageService.uploadSlip(
        tenantId,
        {
          userId,
          username: user.username,
          transferAmount: parseFloat(activePaymentAmountValue),
          transferTime,
          transferDate: transferTime,
          slipFile: selectedFile
        }
      )

      if (uploadResult.success) {
        const uploadData = Array.isArray(uploadResult.data) ? uploadResult.data[0] : uploadResult.data
        await submitManualDeposit({
          uploadedSlipUrl: String(uploadData?.storageUrl || ''),
          sourceChannel: 'slip',
        })
      } else {
        toast.error(uploadResult.message || 'เกิดข้อผิดพลาดในการบันทึกสลิป')
      }
    } catch (error: any) {
      console.error('❌ Confirm transfer failed:', error)
      toast.error(error?.message || 'เกิดข้อผิดพลาดในการยืนยันการโอนเงิน')
    } finally {
      setSubmittingManualSlip(false)
    }
  }

  const validateAndSetFile = (file: File, onInvalid?: () => void) => {
    if (file) {
      // Check file size
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        toast.error('ขนาดไฟล์สลิปไม่ควรเกิน 2MB')
        onInvalid?.()
        return
      }

      // Some Android/gallery pickers return an empty or generic MIME type for
      // real bank slip images. Trust the extension as a fallback and let the
      // backend/SlipOK perform the actual slip verification.
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
      const allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp']
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))
      const hasAllowedMimeType = allowedTypes.includes(String(file.type || '').toLowerCase())
      const hasAllowedExtension = allowedExtensions.includes(fileExtension)
      if (!hasAllowedMimeType && !hasAllowedExtension) {
        toast.error('กรุณาอัปโหลดไฟล์รูปภาพเท่านั้น (JPG, PNG, GIF, WebP)')
        onInvalid?.()
        return
      }

      if (!allowedExtensions.includes(fileExtension)) {
        toast.error('นามสกุลไฟล์ไม่ถูกต้อง กรุณาอัปโหลดไฟล์รูปภาพเท่านั้น')
        onInvalid?.()
        return
      }

      // Check if file name contains suspicious patterns
      const suspiciousPatterns = [
        'script', 'javascript', 'php', 'asp', 'jsp', 'exe', 'bat', 'cmd', 'com', 'pif',
        'scr', 'vbs', 'wsf', 'wsh', 'ps1', 'sh', 'bash', 'sql', 'html', 'htm', 'xml'
      ]
      const fileName = file.name.toLowerCase()
      const hasSuspiciousPattern = suspiciousPatterns.some(pattern => fileName.includes(pattern))
      
      if (hasSuspiciousPattern) {
        toast.error('ชื่อไฟล์ไม่เหมาะสม กรุณาเปลี่ยนชื่อไฟล์และลองใหม่')
        onInvalid?.()
        return
      }

      // Additional validation: Check if file is too small (might be corrupted or fake)
      if (file.size < 1024) { // Less than 1KB
        toast.error('ไฟล์มีขนาดเล็กเกินไป อาจไม่ใช่สลิปที่ถูกต้อง')
        onInvalid?.()
        return
      }

      // Skip validation if enabled (for testing)
      if (skipValidation) {
        setSelectedFile(file)
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl)
        }
        const newPreviewUrl = URL.createObjectURL(file)
        setPreviewUrl(newPreviewUrl)
        toast.success('ไฟล์สลิปถูกต้อง (ข้ามการตรวจสอบ)')
        return
      }

      setSelectedFile(file)
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
      const newPreviewUrl = URL.createObjectURL(file)
      setPreviewUrl(newPreviewUrl)
      toast.success('เลือกไฟล์สลิปแล้ว สามารถดำเนินการต่อได้')
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    validateAndSetFile(file, () => {
      e.target.value = ''
    })
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(false)

    const file = e.dataTransfer.files?.[0]
    if (!file) return

    validateAndSetFile(file, () => {
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    })
  }

  const removeFile = () => {
    setSelectedFile(null)
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
      setPreviewUrl(null)
    }
    // Clear the file input
    const fileInput = document.getElementById('formFile') as HTMLInputElement
    if (fileInput) {
      fileInput.value = ''
    }
    toast.success('ลบไฟล์เรียบร้อยแล้ว')
  }

  if (isPaymentMode) {
    return (
      <div className="content pb-3">
        <div className="container">
          <div className="row px--1">
            <div className="col-12 px-1">
              <div className="bdr-5 bx-sd mb-2">
                <div className="row mx--1">
                  <div className="col-12 px-1">
                    <div className="w-100 p-2 bgc-4 wrap justify-content-between">
                      <a
                        href="#"
                        className="txt-link-d"
                        onClick={(e) => {
                          e.preventDefault()
                          navigate('/topup')
                        }}
                      >
                        <i className="fas fa-chevron-left me-1"></i>
                        หน้าหลัก
                      </a>
                    </div>
                  </div>

                  <div className="col-12 px-1">
                    <div className="w-100 p-2 bgc-7">
                      <div className="bdr-5 bgc-4 c-red px-2 py-1 fs-18 t-medium mb-2">
                        <i className="fas fa-qrcode me-2"></i>
                        ธนาคาร
                      </div>

                      <div className="row mx--1 mb-2">
                        {['เลือกยอดฝาก', 'ชำระผ่านธนาคาร', 'แนบสลิป'].map((label, index) => (
                          <div key={label} className="col-12 col-lg-4 px-1 mb-2">
                            <div
                              className="bdr-5 p-3 h-100"
                              style={{
                                background: index === 1
                                  ? 'linear-gradient(135deg, #2563eb 0%, #38bdf8 100%)'
                                  : '#f8fafc',
                                color: index === 1 ? '#fff' : '#0f172a',
                                border: '1px solid rgba(148,163,184,0.2)'
                              }}
                            >
                              <small className="d-block mb-1" style={{ opacity: 0.8 }}>Step {index + 1}</small>
                              <div className="t-medium">{label}</div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="bdr-5 bgc-4 c-red p-2 mb-2">
                        <div className="row mx--1">
                          <div className="col-12 px-1 mb-2">
                            <div className="c-dark t-medium">ข้อมูลรายการฝาก</div>
                          </div>

                          <div className="col-12 col-lg-4 px-1 mb-2">
                            <div className="bgc-7 bdr-5 p-3 h-100">
                              <small className="c-muted d-block mb-1">จำนวนเงิน</small>
                              <h3 className="c-red t-medium mb-0">
                                {isPaymentMode ? activePaymentAmountDisplay : `${Number(amount || 0).toLocaleString()} บาท`}
                              </h3>
                            </div>
                          </div>

                          <div className="col-12 col-lg-4 px-1 mb-2">
                            <div className="bgc-7 bdr-5 p-3 h-100">
                              <small className="c-muted d-block mb-1">ช่องทางฝาก</small>
                              <h5 className="c-dark t-medium mb-0">ธนาคาร</h5>
                            </div>
                          </div>

                          <div className="col-12 col-lg-4 px-1 mb-2">
                            <div className="bgc-7 bdr-5 p-3 h-100">
                              <small className="c-muted d-block mb-1">บัญชีของคุณ</small>
                              <h6 className="c-dark mb-1">{userBankAccount?.bankName || '-'}</h6>
                              <div className="c-red t-medium">{userBankAccount?.accountNumber || '-'}</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bdr-5 bgc-4 c-red p-2 mb-2">
                        <div className="row mx--1">
                          <div className="col-12 px-1 mb-2">
                            <div className="c-dark t-medium">QR ชำระเงิน</div>
                          </div>

                          {paymentLoading && (
                            <div className="col-12 px-1 mb-2">
                              <div className="bgc-7 bdr-5 p-4 text-center">
                                <div className="c-dark">กำลังสร้าง QR ธนาคาร...</div>
                              </div>
                            </div>
                          )}

                          {!paymentLoading && paymentError && (
                            <div className="col-12 px-1 mb-2">
                              <div className="bgc-7 bdr-5 p-4 text-center">
                                <div className="text-danger mb-3">{paymentError}</div>
                                <button className="check-button bgg-1 c-white px-4" onClick={() => void requestPaymentQr()}>
                                  ลองใหม่
                                </button>
                              </div>
                            </div>
                          )}

                          {!paymentLoading && !paymentError && (
                            <>
                              {paymentReuseSecondsLeft > 0 && (
                                <div className="col-12 px-1 mb-2">
                                  <div
                                    className="bdr-5 p-3"
                                    style={{
                                      background: 'linear-gradient(180deg, rgba(255,251,235,0.98) 0%, rgba(254,243,199,0.95) 100%)',
                                      border: '1px solid rgba(245, 158, 11, 0.28)',
                                    }}
                                  >
                                    <small className="d-block mb-1" style={{ color: '#92400e', fontWeight: 700 }}>
                                      {paymentGateway?.wasReused ? 'กำลังใช้รายการธนาคารเดิม' : 'บัญชีนี้อยู่ในช่วง reuse รายการเดิม'}
                                    </small>
                                    <div style={{ color: '#b45309', fontWeight: 800, fontSize: '1.2rem' }}>{paymentReuseCountdownText}</div>
                                    <div className="mt-2 fs-12" style={{ color: '#78350f' }}>
                                      ระบบจะ reuse รายการเดิมตามเลขบัญชีและธนาคารของผู้ใช้ เพื่อให้ตรงกับข้อจำกัดของ provider
                                    </div>
                                  </div>
                                </div>
                              )}

                              <div className="col-12 col-lg-6 px-1 mb-2">
                                <div className="bgc-7 bdr-5 p-3 h-100 text-center">
                                  {hasPaymentQr ? (
                                    <>
                                      <div className="d-inline-flex rounded-4 bg-white p-3 shadow-sm">
                                        {paymentQrImage ? (
                                          <img
                                            src={paymentQrImage}
                                            alt="Bank payment QR"
                                            className="img-fluid bdr-5"
                                            style={{ maxWidth: '100%', maxHeight: '320px', objectFit: 'contain' }}
                                          />
                                        ) : (
                                          <QRCodeSVG value={paymentQrRawData} size={220} level="M" includeMargin />
                                        )}
                                      </div>
                                      <div className="c-dark fs-12 mt-2">สแกน QR นี้เพื่อชำระเงินตามยอดของรายการธนาคารนี้</div>
                                      {paymentQrLink && (
                                        <div className="mt-2">
                                          <a href={paymentQrLink} target="_blank" rel="noreferrer">
                                            เปิดลิงก์ชำระเงิน
                                          </a>
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <div className="c-dark">ระบบสร้างรายการ payment แล้ว แต่ยังไม่ได้รับ QR image จาก provider</div>
                                  )}
                                </div>
                              </div>

                              <div className="col-12 col-lg-6 px-1 mb-2">
                                <div className="bgc-7 bdr-5 p-3 h-100">
                                  <small className="c-muted d-block mb-1">ช่องทางชำระเงิน</small>
                                  <h5 className="c-dark t-medium mb-3">ธนาคาร</h5>

                                  <small className="c-muted d-block mb-1">เลขอ้างอิงธนาคาร</small>
                                  <div className="c-red t-medium mb-3">{paymentGateway?.externalRefId || paymentTransaction?._id || '-'}</div>

                                  {paymentGateway?.bank && paymentGateway?.accountNumber && (
                                    <>
                                      <small className="c-muted d-block mb-1">บัญชีสำหรับชำระเงิน</small>
                                      <div className="c-dark mb-1">{paymentGateway.bank}</div>
                                      <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <div className="c-red t-medium">{paymentGateway.accountNumber}</div>
                                        <button
                                          className="button-copy wrap bdr-5 border-0 c-dark py-1"
                                          onClick={() => copyToClipboard(paymentGateway.accountNumber || '')}
                                        >
                                          <i className="fas fa-link me-1"></i>
                                          <small>คลิกคัดลอก</small>
                                        </button>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="bdr-5 bgc-4 c-red p-2 mb-2">
                        <div className="row mx--1">
                          <div className="col-12 px-1 mb-2">
                            <div className="c-dark t-medium">Step 3 แนบสลิป</div>
                          </div>
                          <div className="col-12 px-1 mb-2">
                            <div className="alert alert-info fs-12 mb-0">
                              หลังชำระเงินผ่าน QR แล้ว กรุณาแนบสลิปเพื่อส่งรายการฝากให้เป็นมาตรฐานเดียวกัน
                            </div>
                          </div>
                          <div className="col-12 px-1 mb-2">
                            <div
                              className="bgc-7 p-2 bdr-5"
                              onDragOver={(e) => {
                                e.preventDefault()
                                setIsDragOver(true)
                              }}
                              onDragLeave={(e) => {
                                e.preventDefault()
                                setIsDragOver(false)
                              }}
                              onDrop={handleDrop}
                              style={isDragOver ? { outline: '2px dashed #00a16f', background: '#eefaf6' } : undefined}
                            >
                              <input
                                ref={fileInputRef}
                                className="form-control"
                                type="file"
                                id="formFile"
                                accept="image/*"
                                onChange={handleFileChange}
                              />
                              <div className="text-center mt-2">
                                <small className={isDragOver ? 'c-dark' : 'c-muted'}>
                                  <i className="fas fa-cloud-upload-alt me-1"></i>
                                  ลากรูปมาวางที่นี่ หรือคลิกเพื่อเลือกไฟล์
                                </small>
                              </div>
                            </div>
                          </div>
                          {previewUrl && selectedFile && (
                            <div className="col-12 px-1 mb-2">
                              <div className="bgc-7 bdr-5 p-3 text-center">
                                <img
                                  src={previewUrl}
                                  alt="Preview of uploaded slip"
                                  className="img-fluid bdr-5 shadow-sm"
                                  style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain', border: '2px solid #ddd' }}
                                />
                                <div className="mt-2">
                                  <small className="c-dark">{selectedFile.name}</small>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="row mx--1">
                        <div className="col-12 col-lg-6 px-1 mb-2">
                          <button
                            className="check-button w-100 bgg-1 c-white"
                            onClick={handleAttachPaymentSlip}
                            disabled={submittingPaymentSlip || paymentLoading || !paymentTransaction?._id}
                          >
                            {submittingPaymentSlip ? 'กำลังแนบสลิป...' : 'ยืนยันการชำระเงินและแนบสลิป'}
                          </button>
                        </div>
                        <div className="col-12 col-lg-6 px-1 mb-2">
                          <button
                            className="check-button w-100"
                            style={{ background: '#e2e8f0', color: '#0f172a' }}
                            onClick={() => navigate('/topup')}
                          >
                            กลับไปแก้ไขยอด
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="content pb-3">
      <div className="container">
        <div className="row px--1">
          <div className="col-12 px-1">
            <div className="bdr-5 bx-sd mb-2">
              <div className="row mx--1">
                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-4 wrap justify-content-between">
                    <a 
                      href="#" 
                      className="txt-link-d"
                      onClick={(e) => {
                        e.preventDefault()
                        navigate('/topup')
                      }}
                    >
                      <i className="fas fa-chevron-left me-1"></i>
                      หน้าหลัก
                    </a>
                  </div>
                </div>

                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-7">
                    <div className="bdr-5 bgc-4 c-red px-2 py-1 fs-18 t-medium mb-2">
                      <i className="fas fa-wallet me-2"></i>
                      {depositMethod === 'decimal_reservation'
                        ? 'ฝากทศนิยม'
                        : depositMethod === 'prompt_pay'
                          ? 'PromptPay QR พร้อมชำระ'
                          : depositMethod === 'true_money'
                            ? 'แจ้งเติมเครดิต (ทรูมันนี่)'
                            : 'แจ้งเติมเครดิต'}
                    </div>

                    <div className="bdr-5 bgc-4 c-red p-2 mb-2">
                      <div className="row mx--1">
                        <div className="col-12 px-1 mb-2">
                          <div className="c-dark t-medium">
                            {isPromptPayMode
                              ? 'ข้อมูล PromptPay ที่ใช้สร้าง QR'
                              : isTrueMoneyMode
                                ? 'ข้อมูลบัญชี TrueMoney ที่ต้องโอน'
                                : showReservationCountdown
                                  ? 'ข้อมูลบัญชีธนาคารที่ต้องโอน'
                                  : 'ข้อมูลบัญชีธนาคารที่ต้องโอน'}
                          </div>
                        </div>

                        {fallbackNotice && (
                          <div className="col-12 px-1 mb-2">
                            <div className="alert alert-warning fs-12 mb-0">
                              {fallbackNotice}
                            </div>
                          </div>
                        )}

                        <div className="col-12 col-lg-6 px-1 mb-2">
                          <div className="wrap align-items-start bgc-7 bdr-5 p-2">
                            <div className="img-b bdr-5 bgc-4">
                              {/* Display user bank icon if available, otherwise default */}
                              {userBankAccount?.imageUrl ? (
                                <img
                                  src={userBankAccount.imageUrl}
                                  alt={userBankAccount.bankName}
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement
                                    target.src = getImageUrl('icons-default-bank.png')
                                  }}
                                />
                              ) : (
                                <img src={getImageUrl('icons-default-bank.png')} alt="User Bank" />
                              )}
                            </div>

                            <div className="txt-b ms-3">
                              <h6
                                className="c-white bgg-4 bdr-5 px-2 mb-1"
                                style={{ width: 'fit-content' }}
                              >
                                โอนจากบัญชี
                              </h6>
                              <h6 className="c-dark">{userBankAccount?.bankName || 'บัญชีของคุณ'}</h6>
                              <div className="d-flex align-items-center gap-2">
                                <h5 className="c-red t-medium mb-0">{userBankAccount?.accountNumber || '**********'}</h5>
                                {/* <button 
                                  className="button-copy wrap bdr-5 border-0 c-dark py-1"
                                  onClick={() => copyToClipboard(userBankAccount?.accountNumber || '')}
                                >
                                  <i className="fas fa-link me-1"></i>
                                  <small> คลิกคัดลอก </small>
                                </button> */}
                              </div>
                              <h6
                                className="c-white bgg-5 bdr-5 px-2 mb-1"
                                style={{ width: 'fit-content' }}
                              >
                                ชื่อบัญชี
                              </h6>
                              <h5 className="c-dark t-medium fs-16">
                                {userBankAccount?.accountName || user?.username || 'ผู้ใช้'}
                              </h5>
                            </div>
                          </div>
                        </div>

                        {!isPromptPayMode && (
                          <div className="col-12 col-lg-6 px-1 mb-2">
                            <div className="wrap align-items-start bgc-7 bdr-5 p-2 position-relative">
                              <div className="img-b bdr-5 bgc-4">
                              {isTrueMoneyMode || (resolvedDepositAccount && isTrueMoneyDepositAccount(resolvedDepositAccount)) ? (
                                  <img src={truemoneyLogo} alt="TrueMoney Wallet" />
                                ) : resolvedDepositAccount?.bankIcon ? (
                                  <img 
                                    src={resolvedDepositAccount.bankIcon} 
                                    alt={resolvedDepositAccount.bankName}
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement
                                      target.src = getImageUrl('icons-default-bank.png')
                                    }}
                                  />
                                ) : (
                                  <img src={getImageUrl('icons-default-bank.png')} alt="Default Bank" />
                                )}
                              </div>

                              <button 
                                className="button-copy wrap bdr-5 border-0 c-dark py-1"
                                onClick={() => copyToClipboard(resolvedDepositAccount?.accountNumber || '')}
                              >
                                <i className="fas fa-link me-1"></i>
                                <small> คลิกคัดลอก </small>
                              </button>

                              <div className="txt-b ms-3">
                                <h6
                                  className="c-white bgg-4 bdr-5 px-2 mb-1"
                                  style={{ width: 'fit-content' }}
                                >
                                  โอนถึงบัญชี
                                </h6>
                                <h6 className="c-dark">{resolvedDepositAccount?.bankName || 'ธนาคาร'}</h6>
                                <h5 className="c-red t-medium">
                                  {resolvedDepositAccount?.accountNumber || '**********'}
                                </h5>
                                <h6
                                  className="c-white bgg-5 bdr-5 px-2 mb-1"
                                  style={{ width: 'fit-content' }}
                                >
                                  ชื่อบัญชี
                                </h6>
                                <h5 className="c-dark t-medium fs-16">
                                  {resolvedDepositAccount?.accountName || 'เจ้าของเว็บ'}
                                </h5>
                              </div>
                            </div>
                          </div>
                        )}

                        {isPromptPayMode && promptPayQrValue && (
                          <div className="col-12 px-1 mb-2">
                            <div className="bgc-7 bdr-5 p-3 text-center">
                              <div className="c-dark t-medium mb-2">QR PromptPay</div>
                              <div className="d-inline-flex rounded bg-white p-3 shadow-sm">
                                <QRCodeSVG value={promptPayQrValue} size={240} level="M" includeMargin />
                              </div>
                              <div className="c-dark fs-12 mt-2">
                                สแกน QR นี้เพื่อโอนผ่าน PromptPay ตามยอดที่เลือก โดยไม่ผ่าน Payment Gateway
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="row mx--1">
                        <div className="col-12 px-1 mb-2">
                          <div className="text-center pt-5 p-2 bdr-5 bdc-1 bgg-1 position-relative">
                            <button 
                              className="button-copy wrap bdr-5 border-0 c-dark py-1"
                              onClick={() => copyToClipboard(amount)}
                            >
                              <i className="fas fa-link me-1"></i>
                              <small> คลิกคัดลอก </small>
                            </button>

                            <h5 className="c-yellow">
                              กรุณาโอนเงินจำนวนยอดนี้เท่านั้น
                            </h5>
                            <h1 className="t-medium c-white">{amount}</h1>

                            {showReservationCountdown && (
                              <>
                                <h6 className="t-regular c-white">โอนก่อนหมดเวลา</h6>

                                <div className="bgc-4 bdr-5 mt-2">
                                  <h5 className="c-red">{formatTime(timeLeft)}</h5>
                                </div>

                                <h6 className="t-regular c-white mt-2">เหลือ(นาที)</h6>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="col-12 px-1 mb-2">
                          <div className="bgc-7 bdr-5 px-2 py-2 mb-2">
                            {allowManualSlipAttachment ? (
                              <>
                                <p className="c-dark mb-2">
                                  แนบไฟล์ (สลิป)
                                  <span className="ms-1 c-red fs-14">
                                    {submitWithoutSlipAllowed ? '(ไม่บังคับ)' : '(บังคับ)'}
                                  </span>
                                  <span className="c-red fs-14">
                                    *** ขนาดไฟล์สลิปไม่ควรเกิน 2MB ***
                                  </span>
                                </p>
                                <div className="alert alert-warning fs-12 mb-2">
                                  <i className="fas fa-shield-alt me-1"></i>
                                  <strong>ข้อควรระวัง:</strong> กรุณาอัปโหลดเฉพาะสลิปการโอนเงินจริงเท่านั้น 
                                  ระบบจะตรวจสอบความถูกต้องของไฟล์อัตโนมัติ
                                </div>
                                <div className="alert alert-info fs-12 mb-2">
                                  <i className="fas fa-history me-1"></i>
                                  หากออกจากหน้านี้ ระบบจะเก็บรายการฝากไว้ให้ และกลับมาแนบสลิปต่อได้จากหน้า `รายการ`
                                </div>
                              </>
                            ) : null}
                            {/* <div className="alert alert-info fs-11 mb-2">
                              <i className="fas fa-info-circle me-1"></i>
                              <strong>เกณฑ์การตรวจสอบ:</strong> รูปภาพต้องมีสัดส่วน 0.8:1 ถึง 8:1, 
                              ขนาดขั้นต่ำ 100x50px, ความคมชัดเหมาะสม และเป็นไฟล์รูปภาพเท่านั้น
                            </div> */}
                            
                            {/* Debug option for testing */}
                            {/* <div className="alert alert-secondary fs-11 mb-2">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="skipValidation"
                                  checked={skipValidation}
                                  onChange={(e) => setSkipValidation(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="skipValidation">
                                  <i className="fas fa-bug me-1"></i>
                                  ข้ามการตรวจสอบ (สำหรับการทดสอบ)
                                </label>
                              </div>
                            </div> */}
                            {allowManualSlipAttachment && (
                              <div
                                className="bgc-4 p-2 bdr-5"
                                onDragOver={(e) => {
                                  e.preventDefault()
                                  setIsDragOver(true)
                                }}
                                onDragLeave={(e) => {
                                  e.preventDefault()
                                  setIsDragOver(false)
                                }}
                                onDrop={handleDrop}
                                style={isDragOver ? { outline: '2px dashed #00a16f', background: '#eefaf6' } : undefined}
                              >
                                <input
                                  ref={fileInputRef}
                                  className="form-control"
                                  type="file"
                                  id="formFile"
                                  accept="image/*"
                                  onChange={handleFileChange}
                                />
                                <div className="text-center mt-2">
                                  <small className={isDragOver ? 'c-dark' : 'c-muted'}>
                                    <i className="fas fa-cloud-upload-alt me-1"></i>
                                    ลากรูปมาวางที่นี่ หรือคลิกเพื่อเลือกไฟล์
                                  </small>
                                </div>
                                {!selectedFile && (
                                  <div className="text-center mt-2">
                                    <small className="c-muted">
                                      <i className="fas fa-upload me-1"></i>
                                      ยังไม่ได้เลือกไฟล์
                                    </small>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Image Preview Section */}
                            {allowManualSlipAttachment && previewUrl && selectedFile && (
                              <div className="mt-3">
                                <div className="bgc-7 bdr-5 p-3">
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="c-dark mb-0">
                                      <i className="fas fa-image me-2"></i>
                                      ตัวอย่างสลิปที่อัปโหลด
                                    </h6>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={removeFile}
                                      title="ลบไฟล์"
                                    >
                                      <i className="fas fa-trash"></i>
                                    </button>
                                  </div>
                                  
                                  <div className="text-center">
                                    <img
                                      src={previewUrl}
                                      alt="Preview of uploaded slip"
                                      className="img-fluid bdr-5 shadow-sm"
                                      style={{
                                        maxWidth: '100%',
                                        maxHeight: '300px',
                                        objectFit: 'contain',
                                        border: '2px solid #ddd'
                                      }}
                                    />
                                  </div>
                                  
                                  <div className="mt-2 text-center">
                                    <small className="c-dark">
                                      <i className="fas fa-file-image me-1"></i>
                                      {selectedFile.name}
                                    </small>
                                    <br />
                                    <small className="c-muted">
                                      ขนาด: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                    </small>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          <h6 className="c-dark">
                            กรุณาตรวจสอบข้อมูลของท่านให้ถูกต้อง
                            {!isSmsOnlyManualDeposit && (
                              <>
                                {' '}และกดปุ่ม
                                <span className="c-red">ยืนยันการแจ้งโอนเงิน</span>
                              </>
                            )}
                          </h6>
                        </div>

                        <div className="col-12 px-1">
                          {allowManualSlipAttachment && (
                            <button
                              className="check-button w-100 bgg-1 bdc-1 c-white"
                              onClick={handleConfirm}
                              disabled={submittingManualSlip || !selectedFile}
                            >
                              {submittingManualSlip ? 'กำลังยืนยันการโอนเงิน...' : 'ยืนยันการโอนเงิน'}
                            </button>
                          )}
                        </div>
                        {isSmsOnlyManualDeposit ? (
                          <div className="col-12 px-1 mt-2">
                            <button
                              type="button"
                              className="check-button w-100 bgc-4 border-0 c-red"
                              onClick={() => navigate('/')}
                            >
                              กลับไปหน้าแรก
                            </button>
                          </div>
                        ) : submitWithoutSlipAllowed && (
                          <div className="col-12 px-1 mt-2">
                            <button
                              type="button"
                              className="check-button w-100 bgc-4 border-0 c-red"
                              onClick={handleConfirmWithoutSlip}
                            >
                              ส่งรายการฝากโดยยังไม่แนบสลิป
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
    </div>
  )
}

export default TopupConfirmPage
